/* ----------------------------------------------------------------------------
   M0 · Courier Partner 360  (route /supply/partners/:id)
   React port of v6 render360() (mockup lines 2524-2712): header card with avatar,
   status, legal/verticals/integration/opType + GST, onboarding bar with a "View
   checklist" toggle (inline checklist panel, met/unmet items + fix links — honours
   the ?chk=1 deep-link from the directory) and admin Edit / Activate-Deactivate
   actions. A 6-card KPI strip (Total / Active Agreements · Codes Active / Total ·
   Open Disputes · COD SLA) whose cards jump to tabs. Vertical section menu with
   six tabs: Overview (Basic Details + POC Directory table + Org Hierarchy) ·
   Courier Codes (grouped, expandable with per-code agreement history) · Agreements
   (timeline) · Rate Cards (commercial) · Billing (invoice + payment history) ·
   Performance. POC slide-over drawer (view / edit / add) opened from the POC table
   and the org-tree nodes.
   ---------------------------------------------------------------------------- */
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import SettingsInputComponentOutlinedIcon from '@mui/icons-material/SettingsInputComponentOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import CheckIcon from '@mui/icons-material/Check'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import PageTitle from '../../../components/PageTitle'
import {Box, Typography, Stack, Button, Chip} from '@mui/material'
import {alpha} from '@mui/material/styles'
import {Badge, KpiStrip, SupplyTable, SideDrawer, ConfirmDialog, useToast, SearchSelect, HERO_BTN, HERO_BTN_SOLID, LogicalName} from '../components'
import {scoreColor, initials, cx, fmtDate, inr, serviceTypeChipData} from '../components'
import {apiError, AGREEMENT_TYPE_LABEL, INTEGRATION_ENUM, normView} from '../supplyMeta'
import {
  useGetParentQuery,
  useCodesByParentQuery,
  useAgreementTimelineQuery,
  useLazyParentRateCardsQuery,
  useLazyCourierInvoicesQuery,
  useLazyDownloadAgreementQuery,
  useLazyZoneGetSetupQuery,
  useUpdateParentMutation,
  useUpdateParentStatusMutation,
} from '../../../redux/supplyApiSlice'
import {mapParent, mapCode, codeAgreementBadgeFromTimeline, itemCoversCode} from './mapping'
import {StatusPill, checklistState, codesState, agreementState, rateCardState, zoneState} from '../onboardingStatus'
import PocDrawer from './PocDrawer'

const ICO = {fontSize: 16}

/* ───────── onboarding completeness checklist (mockup scoreComponents),
   fed from real data: parent fields + contacts + codes + agreement timeline ───────── */
function scoreComponents(p, codes, timeline) {
  const pocs = (p.pocs || []).filter((c) => c.status !== 'Inactive')
  const has = (r) => pocs.some((c) => (c.roles || []).some((x) => x.indexOf(r) >= 0))
  const hasD = (p.verticals || []).indexOf('D2C') >= 0
  const hasP = (p.verticals || []).indexOf('PTL') >= 0
  const activeCodes = (codes || []).filter((c) => c.status === 'Active').length
  const agrSigned2 = (timeline || []).some(
    (t) => t.agreement && t.agreement.stage === 'SIGNED' && t.agreement.status === 'ACTIVE'
  )
  return [
    {key: 'basics', label: 'Parent basics (name, legal, integration)', weight: 15, met: !!(p.name && p.legal && p.integration), act: 'basics', fix: 'Complete parent basic details'},
    {key: 'gst', label: 'GST number', weight: 5, met: !!p.gst, act: 'basics', fix: 'Add GST number'},
    {key: 'integration', label: 'Integration method', weight: 5, met: !!p.integration, act: 'basics', fix: 'Set integration method'},
    {key: 'kam', label: 'Key Account Manager (KAM)', weight: 10, met: has('Key Account'), act: 'poc', fix: 'Add a KAM POC'},
    {key: 'accountHead', label: 'Account Head', weight: 5, met: has('Account Head'), act: 'poc', fix: 'Add an Account Head POC'},
    {key: 'businessHead', label: 'Business Head / VP', weight: 5, met: has('Business Head'), act: 'poc', fix: 'Add a Business Head POC'},
    {key: 'weight', label: 'Weight Discrepancy POC', weight: 5, met: has('Weight'), act: 'poc', fix: 'Add a Weight Discrepancy POC'},
    {key: 'cod', label: 'COD Remittance POC', weight: 5, met: has('COD'), act: 'poc', fix: 'Add a COD Remittance POC'},
    {key: 'performance', label: 'Performance Escalation POC', weight: 5, met: has('Performance'), act: 'poc', fix: 'Add a Performance Escalation POC'},
    {key: 'minPocs', label: 'At least 3 POCs', weight: 5, met: pocs.length >= 3, act: 'poc', fix: 'Add more POCs (minimum 3)'},
    {key: 'codD2C', label: 'COD SLA — D2C', weight: 5, met: !hasD || !!p.codD2C, act: 'cod', fix: 'Set the D2C COD SLA'},
    {key: 'codPTL', label: 'COD SLA — PTL', weight: 5, met: !hasP || !!p.codPTL, act: 'cod', fix: 'Set the PTL COD SLA'},
    {key: 'activeCode', label: 'At least one active code', weight: 15, met: activeCodes > 0, act: 'codes', fix: 'Activate a courier code'},
    {key: 'agreement', label: 'Signed agreement (Stage 2)', weight: 5, met: agrSigned2, act: 'agreement', fix: 'Add a signed (Stage 2) agreement'},
  ]
}

/* status pill — semantic tones (Active / Draft / Inactive) */
const STATUS_TONE = {
  Active: {l: 'Active', fg: '#166534', bg: '#DCFCE7'},
  Draft: {l: 'Draft', fg: '#475569', bg: '#F1F5F9'},
  Inactive: {l: 'Inactive', fg: '#991B1B', bg: '#FEE2E2'},
}
function StatusChip({status, reason}) {
  const t = STATUS_TONE[status] || {l: status, fg: '#475569', bg: '#F1F5F9'}
  return (
    <Chip
      label={t.l}
      size='small'
      title={reason ? 'Deactivated — ' + reason : undefined}
      sx={{height: 22, fontSize: 11, fontWeight: 700, bgcolor: t.bg, color: t.fg}}
    />
  )
}

/* Vertical chips reused inside the header + overview. */
function VertChips({verticals}) {
  return (
    <>
      {verticals.map((v, i) => (
        <span
          key={v}
          className={cx('chip', 'chip-' + v.toLowerCase())}
          style={{marginLeft: i ? 6 : 0}}
        >
          {v}
        </span>
      ))}
    </>
  )
}

/* Service-type tags for a code (capped, overflow "+N"). */
function ServiceTypeTags({codes}) {
  const {shown, overflow} = serviceTypeChipData(codes, 3)
  return (
    <span className='wrap' style={{display: 'inline-flex', gap: 5, flexWrap: 'wrap'}}>
      {shown.map((s) => (
        <span className='tag tag-mono' key={s.code} title={s.tip}>
          {s.code}
        </span>
      ))}
      {overflow ? (
        <span className='tag' title={overflow.rest}>
          +{overflow.count}
        </span>
      ) : null}
    </span>
  )
}

/* ───────── Onboarding checklist panel (toggled from the header) ───────── */
function ChecklistPanel({p, comps, onFix}) {
  const unmet = comps.filter((c) => !c.met)
  const met = comps.filter((c) => c.met)
  return (
    <Box>
      <SectionLabel sx={{mb: 1.25}}>Onboarding checklist — {p.score}%</SectionLabel>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: 1.5,
          mb: 1.5,
          borderRadius: 2,
          border: 1,
          borderColor: (t) => alpha(unmet.length ? t.palette.warning.main : t.palette.success.main, 0.3),
          bgcolor: (t) => alpha(unmet.length ? t.palette.warning.main : t.palette.success.main, 0.07),
        }}
      >
        {unmet.length ? <InfoOutlinedIcon sx={{fontSize: 18, color: 'warning.dark', flexShrink: 0}} /> : <CheckCircleOutlinedIcon sx={{fontSize: 18, color: 'success.main', flexShrink: 0}} />}
        <Typography sx={{fontSize: 12.5, color: 'text.primary'}}>
          {unmet.length ? <><b>Missing:</b> {unmet.map((c) => c.label).join(', ')}</> : 'All onboarding checklist items are complete.'}
        </Typography>
      </Box>
      {unmet.map((c) => (
        <Stack
          key={c.key}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          gap={1}
          sx={{py: 0.9, borderBottom: 1, borderColor: 'divider'}}
        >
          <Stack direction='row' spacing={1} alignItems='center'>
            <WarningAmberOutlinedIcon sx={{fontSize: 15, color: 'warning.main'}} />
            <Typography sx={{fontSize: 13, color: 'text.primary'}}>{c.label}</Typography>
            <Typography sx={{fontSize: 11, color: 'text.disabled'}}>+{c.weight}%</Typography>
          </Stack>
          <Button size='small' variant='text' endIcon={<ArrowForwardOutlinedIcon style={{fontSize: 13}} />} onClick={() => onFix(c.act)} sx={{textTransform: 'none', fontWeight: 600, fontSize: 12.5}}>
            {c.fix}
          </Button>
        </Stack>
      ))}
      {met.length ? (
        <>
          <SectionLabel sx={{mt: 1.5, mb: 0.5}}>Completed</SectionLabel>
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, columnGap: 2, rowGap: 0.25}}>
            {met.map((c) => (
              <Stack key={c.key} direction='row' spacing={1} alignItems='center' sx={{py: 0.5}}>
                <CheckCircleOutlinedIcon sx={{fontSize: 14, color: 'success.main'}} />
                <Typography sx={{fontSize: 12.5, color: 'text.secondary'}}>{c.label}</Typography>
              </Stack>
            ))}
          </Box>
        </>
      ) : null}
    </Box>
  )
}

/* ───────── Overview: basics + POC directory table + org tree ───────── */
function OverviewTab({p, onOpenPoc}) {
  return (
    <>
      <Box sx={{...sectionCard, mb: 2}}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{mb: 2}}>
          <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary'}}>Basic Details</Typography>
          <Button size='small' variant='outlined' startIcon={<EditOutlinedIcon style={{fontSize: 15}} />} onClick={() => onOpenPoc(null, 'basics')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
            Edit
          </Button>
        </Stack>
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1.5}}>
          {[
            {icon: <BusinessOutlinedIcon sx={{fontSize: 18}} />, label: 'Legal entity', value: p.legal || '—'},
            {icon: <LocalShippingOutlinedIcon sx={{fontSize: 18}} />, label: 'Freight vertical', value: <VertChips verticals={p.verticals} />},
            {icon: <SettingsInputComponentOutlinedIcon sx={{fontSize: 18}} />, label: 'Integration', value: p.integration || '—'},
            {icon: <ReceiptLongOutlinedIcon sx={{fontSize: 18}} />, label: 'GST', value: <Box component='span' sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 13}}>{p.gst || '—'}</Box>},
            {
              icon: <AttachMoneyOutlinedIcon sx={{fontSize: 18}} />,
              label: 'COD SLA',
              value: [p.codD2C != null ? 'D2C ' + p.codD2C + 'd' : null, p.codPTL != null ? 'PTL ' + p.codPTL + 'd' : null].filter(Boolean).join(' · ') || '—',
            },
            {icon: <ScheduleOutlinedIcon sx={{fontSize: 18}} />, label: 'COD frequency', value: p.codFreq || '—'},
            ...(p.website
              ? [{icon: <LanguageOutlinedIcon sx={{fontSize: 18}} />, label: 'Website', value: (
                  <a className='lnk' href={p.website} target='_blank' rel='noreferrer'>
                    {p.website} <OpenInNewOutlinedIcon style={{fontSize: 12, verticalAlign: '-2px'}} />
                  </a>
                )}]
              : []),
            ...(p.codNotes ? [{icon: <PaymentsOutlinedIcon sx={{fontSize: 18}} />, label: 'COD notes', value: p.codNotes, full: true}] : []),
            {icon: <PlaceOutlinedIcon sx={{fontSize: 18}} />, label: 'Address', value: p.address || '—', full: true},
          ].map((f, i) => (
            <Stack
              key={i}
              direction='row'
              spacing={1.5}
              alignItems='flex-start'
              sx={{gridColumn: f.full ? {sm: '1 / -1'} : 'auto', p: 1.5, borderRadius: 2.5, bgcolor: (th) => alpha(th.palette.text.primary, 0.015), border: 1, borderColor: 'divider'}}
            >
              <Box sx={{width: 34, height: 34, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary'}}>
                {f.icon}
              </Box>
              <Box sx={{minWidth: 0, flex: 1}}>
                <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.secondary'}}>{f.label}</Typography>
                <Box sx={{fontSize: 13.5, fontWeight: 500, color: 'text.primary', lineHeight: 1.5, wordBreak: 'break-word', mt: 0.25}}>{f.value}</Box>
              </Box>
            </Stack>
          ))}
        </Box>
      </Box>

      <PocTable p={p} onOpenPoc={onOpenPoc} />
      <OrgTree p={p} onOpenPoc={onOpenPoc} />
    </>
  )
}

/* soft tonal avatar palette (light tint bg + deep same-hue initials) — modern & calm */
const AVATAR_TONES = [
  {bg: '#E0E7FF', fg: '#4338CA'},
  {bg: '#DBEAFE', fg: '#1D4ED8'},
  {bg: '#CCFBF1', fg: '#0F766E'},
  {bg: '#DCFCE7', fg: '#15803D'},
  {bg: '#FEF9C3', fg: '#A16207'},
  {bg: '#FFE4E6', fg: '#BE123C'},
  {bg: '#F3E8FF', fg: '#7E22CE'},
  {bg: '#FFEDD5', fg: '#C2410C'},
]
const tint = (s) => {
  let h = 0
  const str = String(s || '')
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 997
  return AVATAR_TONES[h % AVATAR_TONES.length]
}
const sectionCard = {border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: 2.5}
const cardHover = {transition: 'all .12s', '&:hover': {borderColor: 'primary.light', bgcolor: (t) => alpha(t.palette.primary.main, 0.03), transform: 'translateY(-1px)'}}

/* ───────── shared MUI primitives for the 360 redesign (no scoped-CSS reuse) ───────── */
function SectionLabel({children, sx}) {
  return (
    <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', ...sx}}>
      {children}
    </Typography>
  )
}
function EmptyState({icon, title, body, action}) {
  return (
    <Box sx={{...sectionCard, textAlign: 'center', py: 6, px: 3}}>
      <Box sx={{width: 56, height: 56, mx: 'auto', mb: 1.5, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', color: 'text.secondary'}}>
        {icon}
      </Box>
      <Typography sx={{fontWeight: 800, fontSize: 16, color: 'text.primary'}}>{title}</Typography>
      {body ? <Typography sx={{fontSize: 13, color: 'text.secondary', mt: 0.75, maxWidth: 460, mx: 'auto', lineHeight: 1.6}}>{body}</Typography> : null}
      {action ? <Box sx={{mt: 2}}>{action}</Box> : null}
    </Box>
  )
}
function LoadingCard({label}) {
  return (
    <Stack spacing={1}>
      {[1, 2, 3, 4].map((i) => (
        <Box key={i} sx={{height: 52, borderRadius: 2.5, bgcolor: 'action.hover', animation: 'pulse 1.2s ease-in-out infinite'}} />
      ))}
      {label ? <Typography sx={{fontSize: 12.5, color: 'text.secondary', textAlign: 'center', mt: 0.5}}>{label}</Typography> : null}
    </Stack>
  )
}
function ErrorCard({message, onRetry}) {
  return (
    <Box sx={{...sectionCard, borderColor: (t) => alpha(t.palette.error.main, 0.35), bgcolor: (t) => alpha(t.palette.error.main, 0.05)}}>
      <Stack direction='row' spacing={1.25} alignItems='flex-start'>
        <WarningAmberOutlinedIcon sx={{fontSize: 18, color: 'error.main', flexShrink: 0, mt: 0.25}} />
        <Box sx={{flex: 1}}>
          <Typography sx={{fontSize: 13, color: 'error.dark'}}>{message}</Typography>
          {onRetry ? (
            <Button size='small' variant='outlined' color='error' onClick={onRetry} sx={{mt: 1, borderRadius: '10px', textTransform: 'none'}}>
              Retry
            </Button>
          ) : null}
        </Box>
      </Stack>
    </Box>
  )
}

/* circular onboarding gauge (premium hero) */
function Gauge({value, color, size = 92, stroke = 9, onDark = false}) {
  const r = (size - stroke) / 2
  const circ = 2 * Math.PI * r
  const v = Math.max(0, Math.min(100, value || 0))
  const off = circ * (1 - v / 100)
  return (
    <Box sx={{position: 'relative', width: size, height: size, flexShrink: 0}}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill='none' stroke={onDark ? 'rgba(255,255,255,.18)' : 'rgba(15,23,42,.08)'} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill='none'
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap='round'
          strokeDasharray={circ}
          strokeDashoffset={off}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{transition: 'stroke-dashoffset .7s cubic-bezier(.4,0,.2,1)'}}
        />
      </svg>
      <Box sx={{position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
        <Typography sx={{fontWeight: 800, fontSize: 20, color: onDark ? '#fff' : 'text.primary', lineHeight: 1}}>{v}%</Typography>
        <Typography sx={{fontSize: 9, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: onDark ? 'rgba(255,255,255,.7)' : 'text.secondary', mt: 0.25}}>Onboarded</Typography>
      </Box>
    </Box>
  )
}

/* premium stat card (replaces the generic KPI strip on this page) */
function StatCard({label, value, sub, icon, accent = '#2065D1', onClick}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: '1 1 170px',
        minWidth: 0,
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all .14s',
        ...(onClick ? {'&:hover': {borderColor: 'primary.light', transform: 'translateY(-2px)', boxShadow: '0 8px 22px rgba(15,23,42,.08)'}} : {}),
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1}>
        <Box sx={{minWidth: 0}}>
          <Typography sx={{fontSize: 26, fontWeight: 800, color: 'text.primary', letterSpacing: '-.5px', lineHeight: 1}}>{value}</Typography>
          <Typography sx={{fontSize: 12, fontWeight: 600, color: 'text.secondary', mt: 0.9}}>{label}</Typography>
          {sub ? <Typography sx={{fontSize: 11, color: 'text.disabled', mt: 0.25}}>{sub}</Typography> : null}
        </Box>
        <Box sx={{width: 36, height: 36, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(accent, 0.12), color: accent}}>{icon}</Box>
      </Stack>
    </Box>
  )
}

/* ───────── Configuration status strip — Rate Card · TAT · Zone, fetched on load ───────── */
const TILE_TONE = {green: {bg: '#DCFCE7', fg: '#166534'}, amber: {bg: '#FEF3C7', fg: '#92400E'}, grey: {bg: '#F1F5F9', fg: '#475569'}}
function StatusTile({icon, label, tone, status, sub, loading, pct}) {
  const t = TILE_TONE[tone] || TILE_TONE.grey
  return (
    <Box sx={{flex: '1 1 220px', minWidth: 0, p: 1.75, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)'}}>
      <Stack direction='row' spacing={1.5} alignItems='center'>
        <Box sx={{width: 44, height: 44, borderRadius: '13px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: t.bg, color: t.fg}}>{icon}</Box>
        <Box sx={{minWidth: 0, flex: 1}}>
          <SectionLabel>{label}</SectionLabel>
          {loading ? (
            <Box sx={{height: 20, width: 96, mt: 0.5, borderRadius: 1, bgcolor: 'action.hover', animation: 'pulse 1.2s ease-in-out infinite'}} />
          ) : (
            <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 0.4}}>
              <Chip size='small' label={status} sx={{height: 21, fontSize: 11, fontWeight: 700, bgcolor: t.bg, color: t.fg}} />
              {sub ? <Typography sx={{fontSize: 11.5, color: 'text.secondary'}}>{sub}</Typography> : null}
            </Stack>
          )}
        </Box>
      </Stack>
      {!loading && pct != null ? (
        <Box sx={{mt: 1.5, height: 5, borderRadius: 99, bgcolor: 'action.hover', overflow: 'hidden'}}>
          <Box sx={{height: '100%', width: Math.max(0, Math.min(100, pct)) + '%', bgcolor: t.fg, borderRadius: 99, transition: 'width .6s ease'}} />
        </Box>
      ) : null}
    </Box>
  )
}

/* Presentational — fed by the top-level setup fetch (rc = parent rate cards,
   zv = per-code normView zone setups; undefined = still loading). */
function SetupStatusStrip({total, rc, zv}) {
  const rcLoading = rc === undefined
  const zLoading = zv === undefined
  const hasRate = (c) => c.activeSla && c.activeSla.sla && Object.values(c.activeSla.sla).some((z) => (z.slab || []).some((s) => s.price != null))
  const hasTat = (c) => c.activeSla && c.activeSla.sla && Object.values(c.activeSla.sla).some((z) => z.minTat != null || z.maxTat != null)
  const rateN = (rc || []).filter(hasRate).length
  const tatN = (rc || []).filter(hasTat).length
  const baseReady = (zv || []).some((v) => v.base && v.base.status === 'READY')
  const pubN = (zv || []).filter((v) => v.lastPublishedAt).length

  const tri = (n) => (total > 0 && n >= total ? ['green', 'Complete'] : n > 0 ? ['amber', 'Partial'] : ['grey', 'Pending'])
  const [rTone, rLabel] = tri(rateN)
  const [tTone, tLabel] = tri(tatN)
  const zone = !baseReady ? ['grey', 'No base'] : pubN >= total && total > 0 ? ['green', 'Published'] : pubN > 0 ? ['amber', 'Partial'] : ['amber', 'Base ready']

  return (
    <Box sx={{mb: 2}}>
      <SectionLabel sx={{mb: 1}}>Configuration status</SectionLabel>
      <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap'}}>
        <StatusTile loading={rcLoading} icon={<AttachMoneyOutlinedIcon />} label='Rate Card' tone={rTone} status={rLabel} sub={total ? rateN + '/' + total + ' codes' : null} pct={total ? (rateN / total) * 100 : 0} />
        <StatusTile loading={rcLoading} icon={<ScheduleOutlinedIcon />} label='Delivery TAT' tone={tTone} status={tLabel} sub={total ? tatN + '/' + total + ' codes' : null} pct={total ? (tatN / total) * 100 : 0} />
        <StatusTile loading={zLoading} icon={<PublicOutlinedIcon />} label='Zone Mapping' tone={zone[0]} status={zone[1]} sub={baseReady && total ? pubN + '/' + total + ' published' : null} pct={!baseReady ? 0 : total ? (pubN / total) * 100 : 0} />
      </Box>
    </Box>
  )
}

/* POC Directory — MUI contact cards; clicking a card opens the slide-over (view). */
function PocTable({p, onOpenPoc}) {
  const pocs = p.pocs || []
  return (
    <Box sx={{...sectionCard, mb: 2}}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1} sx={{mb: 1.75}}>
        <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary'}}>POC Directory</Typography>
        <Stack direction='row' spacing={1.25} alignItems='center'>
          <Typography sx={{fontSize: 12, color: 'text.secondary'}}>{pocs.length} contact{pocs.length === 1 ? '' : 's'}</Typography>
          <Button size='small' variant='outlined' startIcon={<AddCircleOutlineIcon style={{fontSize: 16}} />} onClick={() => onOpenPoc(null, 'add')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
            Add POC
          </Button>
        </Stack>
      </Stack>
      {pocs.length ? (
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 1.25}}>
          {pocs.map((c, i) => {
            const inactive = c.status && c.status !== 'Active'
            return (
              <Box key={c.email || i} onClick={() => onOpenPoc(i, 'view')} sx={{display: 'flex', gap: 1.25, p: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', cursor: 'pointer', opacity: inactive ? 0.7 : 1, ...cardHover}}>
                <Box sx={{width: 38, height: 38, borderRadius: '10px', flexShrink: 0, bgcolor: tint(c.name).bg, color: tint(c.name).fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700}}>
                  {initials(c.name)}
                </Box>
                <Box sx={{minWidth: 0, flex: 1}}>
                  <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap'>
                    <Typography sx={{fontWeight: 700, fontSize: 13.5, color: 'text.primary'}} noWrap>{c.name}</Typography>
                    {inactive ? <Badge status='inactive' /> : null}
                  </Stack>
                  {c.desig ? <Typography sx={{fontSize: 12, color: 'text.secondary'}} noWrap>{c.desig}</Typography> : null}
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap sx={{mt: 0.5}}>
                    {c.roles.map((r) => (
                      <Chip key={r} size='small' label={r.replace(/ \(.+\)/, '')} sx={{height: 18, fontSize: 10, bgcolor: 'action.hover', '& .MuiChip-label': {px: 0.75}}} />
                    ))}
                  </Stack>
                  <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 0.75}}>
                    {c.email ? (
                      <a className='lnk' href={'mailto:' + c.email} onClick={(e) => e.stopPropagation()} style={{fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 3}}>
                        <MailOutlineIcon style={{fontSize: 12}} />
                        {c.email}
                      </a>
                    ) : null}
                    {c.m1 ? <Typography sx={{fontSize: 11.5, color: 'text.secondary', fontFamily: "'JetBrains Mono', monospace"}}>{c.m1}</Typography> : null}
                    {c.linkedin ? (
                      <a className='lnk' href={c.linkedin} target='_blank' rel='noreferrer' onClick={(e) => e.stopPropagation()} style={{display: 'inline-flex'}}>
                        <OpenInNewOutlinedIcon style={{fontSize: 13}} />
                      </a>
                    ) : null}
                  </Stack>
                </Box>
              </Box>
            )
          })}
        </Box>
      ) : (
        <Box sx={{py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 13}}>No POCs captured yet.</Box>
      )}
    </Box>
  )
}

function OrgNode({role, lvl, poc, onClick}) {
  const added = !!poc
  return (
    <Box
      onClick={onClick}
      sx={{
        width: '100%',
        maxWidth: 380,
        p: 1.75,
        borderRadius: 2.5,
        textAlign: 'center',
        cursor: 'pointer',
        border: added ? 1 : '1.5px dashed',
        borderColor: 'divider',
        bgcolor: added ? 'background.paper' : 'transparent',
        ...cardHover,
      }}
    >
      <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>
        {role}{lvl ? ' · L' + lvl : ''}
      </Typography>
      {added ? (
        <>
          <Typography sx={{fontWeight: 700, fontSize: 14, color: 'text.primary', mt: 0.25}}>{poc.name}</Typography>
          {poc.desig ? <Typography sx={{fontSize: 11.5, color: 'text.secondary'}}>{poc.desig}</Typography> : null}
          {poc.email ? (
            <Typography sx={{fontSize: 11, color: 'text.disabled', mt: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
              <MailOutlineIcon style={{fontSize: 11}} />
              {poc.email}
            </Typography>
          ) : null}
        </>
      ) : (
        <Typography sx={{fontSize: 12, color: 'text.disabled', mt: 0.5}}>Not added yet — click to add</Typography>
      )}
    </Box>
  )
}

function OrgTree({p, onOpenPoc}) {
  const findIdx = (key) => p.pocs.findIndex((c) => c.roles.some((r) => r.indexOf(key) >= 0))
  const funcRoles = [
    ['Weight Discrepancy POC', 'Weight'],
    ['COD Remittance POC', 'COD'],
    ['Performance Escalation POC', 'Performance'],
  ]
  const node = (role, lvl, key) => {
    const idx = findIdx(key)
    return (
      <OrgNode role={role} lvl={lvl} poc={idx >= 0 ? p.pocs[idx] : null} onClick={idx >= 0 ? () => onOpenPoc(idx, 'view') : () => onOpenPoc(null, 'add', role)} />
    )
  }
  const connector = <Box sx={{width: 2, height: 18, bgcolor: 'divider'}} />
  return (
    <Box sx={sectionCard}>
      <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary', mb: 2}}>Org Hierarchy</Typography>
      <Stack alignItems='center' spacing={0.5}>
        {node('Business Head', 3, 'Business Head')}
        {connector}
        {node('Account Head', 2, 'Account Head')}
        {connector}
        {node('Key Account Manager', 1, 'Key Account')}
      </Stack>
      <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mt: 2.5, mb: 1}}>
        Functional POCs
      </Typography>
      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr 1fr'}, gap: 1.25}}>
        {funcRoles.map(([label, key]) => {
          const idx = findIdx(key)
          const poc = idx >= 0 ? p.pocs[idx] : null
          return (
            <Box key={label} onClick={idx >= 0 ? () => onOpenPoc(idx, 'view') : () => onOpenPoc(null, 'add', label)} sx={{p: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', cursor: 'pointer', ...cardHover}}>
              <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary'}}>
                {label.replace(' POC', '')}
              </Typography>
              <Typography sx={{fontWeight: 700, fontSize: 13, mt: 0.5, color: poc ? 'text.primary' : 'text.disabled'}}>
                {poc ? poc.name : 'Not added'}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

/* ───────── Edit Details drawer — PATCH partial details via updateParent ───────── */
function EditDetailsDrawer({p, open, onClose, onSaved, toast}) {
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [updateParent] = useUpdateParentMutation()

  useEffect(() => {
    if (!open || !p) return
    setForm({
      name: p.name || '',
      legal: p.legal || '',
      gst: p.gst || '',
      website: p.website || '',
      address: p.address || '',
      integration: p.integration || '',
      codD2C: p.codD2C != null ? String(p.codD2C) : '',
      codPTL: p.codPTL != null ? String(p.codPTL) : '',
      codNotes: p.codNotes || '',
    })
    setSaving(false)
  }, [open, p])

  if (!form) return <SideDrawer open={false} onClose={onClose} title='' />

  const set = (k, v) => setForm((f) => ({...f, [k]: v}))

  const save = () => {
    if (!form.name.trim() || !form.legal.trim())
      return toast('Name and legal entity are required.', 'error')
    const cod = p.raw && p.raw.codConfig ? p.raw.codConfig : {}
    const details = {
      name: form.name.trim(),
      legalEntityName: form.legal.trim(),
      gstNumber: form.gst.trim() || null,
      website: form.website.trim() || null,
      registeredAddress: form.address.trim() || null,
      integrationMethod: INTEGRATION_ENUM[form.integration] || form.integration || null,
      codConfig: {
        ...cod,
        slaD2cDays: form.codD2C === '' ? null : Number(form.codD2C),
        slaPtlDays: form.codPTL === '' ? null : Number(form.codPTL),
        notes: form.codNotes || null,
      },
    }
    setSaving(true)
    updateParent({id: p.id, details})
      .unwrap()
      .then(() => {
        toast('Partner details updated.', 'success')
        onClose()
        onSaved() // updateParent invalidates the Parent cache → 360 view auto-refreshes
      })
      .catch((e) => toast(apiError(e), 'error'))
      .finally(() => setSaving(false))
  }

  const field = (label, k, props = {}) => (
    <div className='field' key={k}>
      <label>
        {label} {props.opt ? <span className='opt'>(optional)</span> : <span className='req'>*</span>}
      </label>
      {props.type === 'textarea' ? (
        <textarea className='input' rows={2} value={form[k]} onChange={(e) => set(k, e.target.value)} />
      ) : (
        <input className='input' value={form[k]} placeholder={props.ph || label} onChange={(e) => set(k, e.target.value)} />
      )}
    </div>
  )

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={'Edit Details — ' + p.name}
      width={560}
      footer={
        <>
          <button className='btn' onClick={onClose}>
            Cancel
          </button>
          <button className='btn btn-primary' onClick={save} disabled={saving}>
            <CheckIcon style={ICO} />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </>
      }
    >
      <div className='grid' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
        {field('Partner name', 'name')}
        {field('Legal entity', 'legal')}
        {field('GST number', 'gst', {opt: true})}
        {field('Website', 'website', {opt: true, ph: 'https://…'})}
        <div className='field'>
          <label>Integration</label>
          <SearchSelect
            value={form.integration}
            onChange={(v) => set('integration', v)}
            placeholder='—'
            options={Object.keys(INTEGRATION_ENUM)}
          />
        </div>
        {field('COD SLA — D2C (days)', 'codD2C', {opt: true, ph: 'e.g. 7'})}
        {field('COD SLA — PTL (days)', 'codPTL', {opt: true, ph: 'e.g. 15'})}
      </div>
      {field('Registered address', 'address', {opt: true, type: 'textarea'})}
      {field('COD notes', 'codNotes', {opt: true, type: 'textarea'})}
    </SideDrawer>
  )
}

/* ───────── Courier Codes: grouped SupplyTables by vertical ───────── */
function CodeAgrBadge({code, timeline}) {
  const b = codeAgreementBadgeFromTimeline(code.code, timeline)
  return <Badge status={b.key} title={b.tip} />
}

/* agreement display helpers (server timeline item → header / stage / docs) */
const agrHead = (a) =>
  a.type === 'COMMERCIAL_ADDENDUM' || a.type === 'OPERATIONAL_ADDENDUM'
    ? (AGREEMENT_TYPE_LABEL[a.type] || a.type) + (a.addendumNo != null ? ' #' + a.addendumNo : '')
    : AGREEMENT_TYPE_LABEL[a.type] || a.title || a.type
const agrSigned = (a) => a.stage === 'SIGNED'
const lastDoc = (a) => {
  const docs = a.documents || []
  return docs.length ? docs[docs.length - 1] : null
}

function CodeExpand({c, p, timeline, navigate}) {
  const items = (timeline || [])
    .filter((t) => itemCoversCode(t, c.code))
    .slice()
    .sort((x, y) => new Date(y.agreement.effectiveFrom) - new Date(x.agreement.effectiveFrom))
  const meta = (label, val) => (
    <Box>
      <Typography sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.25}}>{label}</Typography>
      <Box sx={{fontSize: 13, color: 'text.primary'}}>{val}</Box>
    </Box>
  )
  return (
    <Box>
      <Stack direction='row' spacing={4} flexWrap='wrap' useFlexGap sx={{mb: 2}}>
        {meta('Prozo ID', <span style={{fontFamily: "'JetBrains Mono', monospace"}}>{c.prozoId || '—'}</span>)}
        {meta('Logical Name', <LogicalName value={c.logicalName} priority={c.priority} />)}
        {meta('Service Types', (c.serviceTypes || []).join(', ') || '—')}
      </Stack>
      <SectionLabel sx={{mb: 1.25}}>Agreement history for this code</SectionLabel>
      {items.length ? (
        <Stack spacing={1.25}>
          {items.map((t) => {
            const a = t.agreement
            const signed = agrSigned(a)
            const accent = signed ? '#16A34A' : '#D97706'
            const doc = lastDoc(a)
            return (
              <Box key={a.id} sx={{position: 'relative', pl: 2, borderRadius: 2, border: 1, borderColor: 'divider', p: 1.75, overflow: 'hidden'}}>
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent}} />
                <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography sx={{fontWeight: 700, fontSize: 13}}>{agrHead(a)}</Typography>
                    <Badge status={signed ? 'signed' : 'pending'} label={signed ? 'Stage 2: Signed' : 'Stage 1: Email received'} />
                  </Stack>
                  <Typography sx={{fontSize: 12, color: 'text.secondary'}}>Effective {fmtDate(a.effectiveFrom)}</Typography>
                </Stack>
                {a.title ? <Typography sx={{fontSize: 13, mt: 0.75}}>{a.title}</Typography> : null}
                <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>
                  {a.appliesToAll ? 'Applies to: All ' + p.name + ' codes' : 'Applies to: ' + (a.linkedCpAccountCodes || []).join(', ')}
                  {doc ? ' · Uploaded by ' + doc.uploadedBy + ' on ' + fmtDate(doc.uploadedAt) : ''}
                </Typography>
              </Box>
            )
          })}
        </Stack>
      ) : (
        <Stack direction='row' spacing={1} alignItems='center' sx={{p: 1.25, borderRadius: 2, bgcolor: 'action.hover', fontSize: 12.5, color: 'text.secondary'}}>
          <WarningAmberOutlinedIcon sx={{fontSize: 16, color: 'warning.main'}} />
          No agreement on file for this code.
        </Stack>
      )}
      <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap sx={{mt: 1.5}}>
        <Button size='small' variant='outlined' onClick={() => navigate('/supply/agreements')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
          View Full Agreement Timeline
        </Button>
        <Button size='small' variant='outlined' onClick={() => navigate('/supply/partners/update?code=' + encodeURIComponent(c.code))} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
          Update Rates &amp; TAT
        </Button>
      </Stack>
    </Box>
  )
}

function CodesTab({p, codes, timeline, navigate}) {
  if (!codes.length) {
    return <EmptyState icon={<SettingsInputComponentOutlinedIcon style={{fontSize: 28}} />} title='No courier codes' body='This partner has no account codes yet.' />
  }
  const columns = [
    {
      key: 'code',
      label: 'Courier Code',
      render: (c) => (
        <a className='lnk mono' href='#' onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/supply/codes/' + encodeURIComponent(c.code)) }}>
          {c.code}
        </a>
      ),
    },
    {key: 'display', label: 'Display Name'},
    {
      key: 'logicalName',
      label: 'Logical Name',
      render: (c) => <LogicalName value={c.logicalName} priority={c.priority} />,
      exportValue: (c) => c.logicalName || '',
    },
    {
      key: 'serviceTypes',
      label: 'Service Types',
      sortable: false,
      render: (c) => <ServiceTypeTags codes={c.serviceTypes} />,
      exportValue: (c) => c.serviceTypes.join('+'),
    },
    {
      key: 'agr',
      label: 'Agreement',
      sortable: false,
      render: (c) => <CodeAgrBadge code={c} timeline={timeline} />,
      exportValue: (c) => codeAgreementBadgeFromTimeline(c.code, timeline).key,
    },
    {key: 'status', label: 'Status', render: (c) => <Badge status={c.status} />},
  ]
  const groups = [
    ['D2C', 'D2C Codes'],
    ['PTL', 'PTL Codes'],
  ]
  return (
    <>
      {groups.map(([v, label]) => {
        const cs = codes.filter((c) => c.vertical === v)
        if (!cs.length) return null
        return (
          <div key={v} style={{marginBottom: 18}}>
            <SectionLabel sx={{m: '6px 0 10px'}}>{label} ({cs.length})</SectionLabel>
            <SupplyTable
              columns={columns}
              rows={cs}
              rowKey={(c) => c.code}
              searchKeys={['code', 'display', 'logicalName']}
              searchPlaceholder='Search codes...'
              pageSize={10}
              exportName={'codes-' + p.id + '-' + v.toLowerCase()}
              rowClick={(c) => navigate('/supply/codes/' + encodeURIComponent(c.code))}
              expand={(c) => <CodeExpand c={c} p={p} timeline={timeline} navigate={navigate} />}
            />
          </div>
        )
      })}
    </>
  )
}

/* ───────── Agreements: server timeline (flags are server-computed) ───────── */
const FLAG_TONE = {red: {bg: '#FEE2E2', fg: '#B91C1C'}, amber: {bg: '#FEF3C7', fg: '#92400E'}, grey: {bg: '#F1F5F9', fg: '#475569'}}
function AgrFlagChips({item}) {
  const chips = []
  if (item.expired) chips.push(['Expired', 'red'])
  else if (item.expiresInDays != null && item.expiresInDays <= 90) chips.push(['Expires in ' + item.expiresInDays + 'd', 'amber'])
  if (item.reviewDueOn) chips.push(['Review due ' + fmtDate(item.reviewDueOn), 'amber'])
  if (item.stage1AgingDays != null && item.stage1AgingDays > 30) chips.push(['Stage 1 aging ' + item.stage1AgingDays + 'd', 'amber'])
  if (item.backdated) chips.push(['Backdated', 'grey'])
  if (item.overlappingIds && item.overlappingIds.length) chips.push(['Overlaps ' + item.overlappingIds.length, 'red'])
  if (!chips.length) return null
  return (
    <>
      {chips.map(([label, t]) => (
        <Chip key={label} size='small' label={label} sx={{height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: FLAG_TONE[t].bg, color: FLAG_TONE[t].fg}} />
      ))}
    </>
  )
}

function AgreementsTab({p, timeline, toast}) {
  const [fetchDownload] = useLazyDownloadAgreementQuery()
  const navigate = useNavigate()
  const items = timeline || []
  if (!items.length) {
    return <EmptyState icon={<DescriptionOutlinedIcon style={{fontSize: 28}} />} title='No agreements' body={'No agreements on record for ' + p.name + '.'} />
  }
  const sorted = items
    .slice()
    .sort((x, y) => new Date(y.agreement.effectiveFrom) - new Date(x.agreement.effectiveFrom))

  const downloadAgr = (a) => {
    fetchDownload({id: a.id})
      .unwrap()
      .then((obj) => {
        if (obj && obj.url) window.open(obj.url, '_blank', 'noopener')
        else toast('No document available for this agreement.', 'info')
      })
      .catch((e) => toast(apiError(e), 'error'))
  }

  return (
    <Box sx={sectionCard}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1} sx={{mb: 2}}>
        <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary'}}>Agreement Timeline · {p.name}</Typography>
        <Button size='small' variant='outlined' startIcon={<OpenInNewOutlinedIcon style={{fontSize: 15}} />} onClick={() => navigate('/supply/agreements')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
          Open in Repository
        </Button>
      </Stack>
      <Stack spacing={1.5}>
        {sorted.map((t) => {
          const a = t.agreement
          const signed = agrSigned(a)
          const accent = signed ? '#16A34A' : '#D97706'
          const doc = lastDoc(a)
          return (
            <Box key={a.id} sx={{position: 'relative', pl: 2, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper', p: 2, overflow: 'hidden'}}>
              <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: accent}} />
              <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1}>
                <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                  <Typography sx={{fontWeight: 700, fontSize: 13.5, color: 'text.primary'}}>{agrHead(a)}</Typography>
                  <Badge status={signed ? 'signed' : 'pending'} label={signed ? 'Stage 2: Signed' : 'Stage 1: Email received'} />
                  {a.status && a.status !== 'ACTIVE' ? <Chip size='small' label={a.status} sx={{height: 20, fontSize: 10.5, bgcolor: 'action.hover'}} /> : null}
                  <AgrFlagChips item={t} />
                </Stack>
                <Typography sx={{fontSize: 12, color: 'text.secondary'}}>
                  Effective {fmtDate(a.effectiveFrom)}{a.effectiveTo ? ' → ' + fmtDate(a.effectiveTo) : ' · open-ended'}
                </Typography>
              </Stack>
              {a.title ? <Typography sx={{fontSize: 13.5, fontWeight: 500, color: 'text.primary', mt: 1}}>{a.title}</Typography> : null}
              <Typography sx={{fontSize: 12, color: 'text.secondary', mt: 0.5}}>
                {a.appliesToAll ? 'Applies to: All codes' : 'Applies to: ' + (a.linkedCpAccountCodes || []).join(', ')}
                {doc ? ' · Uploaded by ' + doc.uploadedBy + ' on ' + fmtDate(doc.uploadedAt) : ''}
                {a.supersedeReason ? ' · Superseded: ' + a.supersedeReason : ''}
              </Typography>
              {a.notes ? <Typography sx={{fontSize: 12, color: 'text.disabled', mt: 0.5}}>{a.notes}</Typography> : null}
              <Stack direction='row' spacing={1} sx={{mt: 1.5}}>
                <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon style={{fontSize: 15}} />} onClick={() => downloadAgr(a)} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
                  Download
                </Button>
                {!signed ? (
                  <Button size='small' variant='contained' disableElevation startIcon={<CheckIcon style={{fontSize: 15}} />} onClick={() => navigate('/supply/agreements')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
                    Update to Stage 2
                  </Button>
                ) : null}
              </Stack>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

/* ───────── Rate Cards (commercial) — real data from GET parent/:id/rateCards ───────── */
const ZONE_NAME = {1: 'Local', 2: 'Regional', 3: 'Metro', 4: 'ROI', 5: 'Special'}
const zoneName = (n) => ZONE_NAME[n] || 'Zone ' + n

/* one row per (account code × zone); codes with no activeSla → single "no card" row */
function buildRateCardRows(cards) {
  const rows = []
  ;(cards || []).forEach((c) => {
    const base = {code: c.cpAccountCode, b2b: !!c.b2bPricingRef}
    const sla = c.activeSla && c.activeSla.sla
    const zoneKeys = sla ? Object.keys(sla) : []
    if (!zoneKeys.length) {
      rows.push({...base, noCard: true, zone: '—', slab: '—', tat: '—', effectiveFrom: null})
      return
    }
    zoneKeys
      .slice()
      .sort((a, b) => Number(a) - Number(b))
      .forEach((zn) => {
        const z = sla[zn] || {}
        const slabs = z.slab || []
        const s0 = slabs[0] || {}
        const start = s0.startWt != null ? s0.startWt : s0.minWt
        rows.push({
          ...base,
          zoneNo: Number(zn),
          zone: zoneName(Number(zn)),
          slab:
            start != null || s0.maxWt != null
              ? (start != null ? start : '—') + '-' + (s0.maxWt != null ? s0.maxWt : '—') + ' gm' + (slabs.length > 1 ? ' +' + (slabs.length - 1) + ' more' : '')
              : '—',
          rate: s0.price,
          tat: z.minTat != null || z.maxTat != null ? (z.minTat != null ? z.minTat : '—') + '-' + (z.maxTat != null ? z.maxTat : '—') + ' ' + (z.tatUnit || 'days').toLowerCase() : '—',
          effectiveFrom: c.activeSla.startDate,
        })
      })
  })
  return rows
}

function RateCardsTab({p, cards, loading, error, onRetry, navigate}) {
  if (loading || (!error && cards == null)) return <LoadingCard label='Loading rate cards…' />
  if (error) return <ErrorCard message={'Could not load rate cards: ' + error} onRetry={onRetry} />
  const rows = buildRateCardRows(cards)
  if (!rows.length) {
    return (
      <EmptyState
        icon={<AttachMoneyOutlinedIcon style={{fontSize: 28}} />}
        title='No rate cards'
        body={'No rate cards captured for ' + p.name + ' yet. Use Rates & TAT Update to add them.'}
        action={
          <Button variant='contained' disableElevation size='small' onClick={() => navigate('/supply/partners/update')} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
            Update Rates &amp; TAT
          </Button>
        }
      />
    )
  }
  const columns = [
    {
      key: 'code',
      label: 'Courier Code',
      render: (r) => (
        <>
          <span className='mono'>{r.code}</span>
          {r.b2b ? (
            <span className='tag' style={{marginLeft: 6}} title='A B2B pricing card exists for this code'>
              B2B card available
            </span>
          ) : null}
        </>
      ),
      exportValue: (r) => r.code,
    },
    {key: 'zone', label: 'Zone'},
    {
      key: 'slab',
      label: 'Weight Slab',
      render: (r) => (r.noCard ? <span className='muted'>No rate card uploaded</span> : r.slab),
    },
    {key: 'rate', label: 'Rate', align: 'right', render: (r) => (r.rate != null ? <b>{inr(r.rate)}</b> : <span className='muted'>—</span>)},
    {key: 'tat', label: 'TAT'},
    {key: 'effectiveFrom', label: 'Effective From', render: (r) => (r.effectiveFrom ? fmtDate(r.effectiveFrom) : <span className='muted'>—</span>)},
  ]
  return (
    <SupplyTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.code + '-' + (r.zoneNo != null ? r.zoneNo : 'none')}
      searchKeys={['code', 'zone']}
      searchPlaceholder='Search rate card...'
      exportName={'rate-card-' + p.id}
    />
  )
}

/* ───────── Billing: invoice history (real finance endpoint) ───────── */
const INVOICE_STATUS_LABEL = {
  FINANCE_UPLOADED: 'Uploaded',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  SETTLED: 'Settled',
}
function invoiceStatusBadge(s) {
  const map = {FINANCE_UPLOADED: 'pending', APPROVED: 'active', REJECTED: 'inactive', SETTLED: 'signed'}
  return <Badge status={map[s] || 'pending'} label={INVOICE_STATUS_LABEL[s] || s || '—'} />
}

function BillingTab({p, invoices, loading, error, onRetry}) {
  return (
    <>
      <SectionLabel sx={{mb: 1.25}}>Invoice History</SectionLabel>
      <div style={{marginBottom: 20}}>
        {loading || (!error && invoices == null) ? (
          <LoadingCard label='Loading invoices…' />
        ) : error ? (
          <ErrorCard message={'Could not load invoices: ' + error} onRetry={onRetry} />
        ) : (
          <SupplyTable
            columns={[
              {key: 'invoiceNumber', label: 'Invoice', render: (i) => <span className='mono'>{i.invoiceNumber || '—'}</span>},
              {key: 'period', label: 'Period', render: (i) => (i.invoiceMonth != null && i.invoiceYear != null ? i.invoiceMonth + '/' + i.invoiceYear : '—'), exportValue: (i) => (i.invoiceMonth != null && i.invoiceYear != null ? i.invoiceMonth + '/' + i.invoiceYear : '')},
              {key: 'totalOrders', label: 'Orders', align: 'right', render: (i) => (i.totalOrders || 0).toLocaleString('en-IN')},
              {key: 'totalAmount', label: 'Amount', align: 'right', render: (i) => <b>{inr(i.totalAmount)}</b>},
              {key: 'reportStatus', label: 'Status', render: (i) => invoiceStatusBadge(i.reportStatus), exportValue: (i) => i.reportStatus},
              {key: 'createdAt', label: 'Created', render: (i) => fmtDate(i.createdAt || i.invoiceDate)},
            ]}
            rows={invoices || []}
            rowKey={(i, idx) => i.invoiceNumber || idx}
            searchKeys={['invoiceNumber']}
            searchPlaceholder='Search invoices...'
            exportName={'invoices-' + p.id}
            emptyText='No invoices for this partner.'
          />
        )}
      </div>
      <SectionLabel sx={{mb: 1.25}}>Payment History</SectionLabel>
      <Box sx={{...sectionCard, color: 'text.secondary', fontSize: 13}}>Payments to couriers are not tracked in Proship yet.</Box>
    </>
  )
}

/* ───────── Performance (placeholder) ───────── */
function PerformanceTab() {
  const kpis = [
    {label: 'Pickup success', value: '—'},
    {label: 'Delivery success', value: '—'},
    {label: 'RTO %', value: '—'},
    {label: 'Weight disc. rate', value: '—'},
  ]
  return (
    <>
      <KpiStrip cards={kpis} />
      <EmptyState
        icon={<LeaderboardOutlinedIcon style={{fontSize: 28}} />}
        title='Performance dashboard coming soon'
        body='Pickup success %, delivery success %, RTO %, transit days by zone, weight discrepancy rate and COD remittance adherence.'
      />
    </>
  )
}

/* ───────── Zone Mapping tab — parent base status + per-account-code zone summary ─────────
   360 is parent-level; zone mapping = one frozen parent base + per-code overrides. We show the
   base status once, then each code's publish state, deep-linking into the full Zone Mapping
   editor (/supply/zone-mapping?code=). Read-only here; editing lives in the dedicated page. */
const BASE_TONE = {
  READY: {bg: '#DCFCE7', fg: '#166534', label: 'Base ready'},
  SEEDING: {bg: '#FEF3C7', fg: '#92400E', label: 'Seeding base…'},
  FAILED: {bg: '#FEE2E2', fg: '#B91C1C', label: 'Seed failed'},
  NONE: {bg: '#F1F5F9', fg: '#475569', label: 'No base yet'},
}
function zoneCodeStatus(baseReady, view) {
  if (!baseReady) return {status: 'inactive', label: 'No base'}
  if (view && view.hasUnpublishedChanges) return {status: 'pending', label: 'Unpublished changes'}
  if (view && view.lastPublishedAt) return {status: 'active', label: 'Published'}
  return {status: 'pending', label: 'Not published'}
}

function ZonesTab({p, codes, navigate}) {
  const [rows, setRows] = useState(null) // null = loading
  const [fetchSetup] = useLazyZoneGetSetupQuery()

  useEffect(() => {
    let on = true
    const list = codes || []
    setRows(null)
    if (!list.length) {
      setRows([])
      return
    }
    Promise.all(
      list.map((c) =>
        fetchSetup(c.code)
          .unwrap()
          .then((raw) => ({c, view: normView(raw)}))
          .catch(() => ({c, view: null}))
      )
    ).then((r) => on && setRows(r))
    return () => {
      on = false
    }
  }, [codes, fetchSetup])

  // parent base — same for every code; take the first loaded view, else the parent doc's zoneBase
  const base = useMemo(() => {
    const v = (rows || []).map((r) => r.view).find((x) => x && x.base && x.base.status !== 'NONE')
    if (v) return v.base
    const zb = p && p.raw && p.raw.zoneBase
    if (zb) return {status: String(zb.status || 'NONE').toUpperCase(), fileCount: zb.fileCount || 0, seededFrom: zb.seededFromProzoId}
    return {status: 'NONE', fileCount: 0}
  }, [rows, p])

  const baseReady = base.status === 'READY'
  const tone = BASE_TONE[base.status] || BASE_TONE.NONE
  const goEdit = (code) => navigate('/supply/zone-mapping?code=' + encodeURIComponent(code || ''))
  const firstCode = codes && codes.length ? codes[0].code : ''

  return (
    <Box>
      {/* parent base panel */}
      <Box sx={{border: 1, borderColor: 'divider', borderRadius: 3, p: 2.5, mb: 2.5, bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)'}}>
        <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2} flexWrap='wrap' useFlexGap>
          <Stack direction='row' spacing={1.5} alignItems='center' sx={{minWidth: 0}}>
            <Box sx={{width: 46, height: 46, borderRadius: '14px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(tone.fg, 0.12), color: tone.fg}}>
              <PublicOutlinedIcon sx={{fontSize: 24}} />
            </Box>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontWeight: 800, fontSize: 15.5, color: 'text.primary', letterSpacing: '-0.2px'}}>Parent base mapping</Typography>
              <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.25}}>
                {baseReady
                  ? `Inherited by every account code under ${p.name} · ${(base.fileCount || 0).toLocaleString('en-IN')} pickup file${base.fileCount === 1 ? '' : 's'}${base.seededFrom ? ' · seeded from ' + base.seededFrom : ''}`
                  : `Seed the parent base from an existing account folder to enable overrides & publishing for ${p.name}.`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Chip label={tone.label} size='small' sx={{bgcolor: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 11.5, height: 24}} />
            <Button size='small' variant='outlined' disabled={!firstCode} onClick={() => goEdit(firstCode)} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
              Open Zone Mapping
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* per-account-code zones */}
      <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.secondary', mb: 1.25}}>
        Per-account-code zones {rows ? `· ${rows.length}` : ''}
      </Typography>

      {rows == null ? (
        <Stack spacing={1}>
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{height: 58, borderRadius: 2.5, bgcolor: 'action.hover', animation: 'pulse 1.2s ease-in-out infinite'}} />
          ))}
        </Stack>
      ) : !rows.length ? (
        <EmptyState
          icon={<PublicOutlinedIcon style={{fontSize: 28}} />}
          title='No account codes under this partner'
          body='Add a courier account code first — each code carries its own zone overrides on top of the parent base.'
        />
      ) : (
        <Stack spacing={1}>
          {rows.map(({c, view}) => {
            const st = zoneCodeStatus(baseReady, view)
            const ov = (view && view.overrides && view.overrides.length) || 0
            return (
              <Box
                key={c.code}
                onClick={() => goEdit(c.code)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  p: 1.75,
                  borderRadius: 2.5,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  transition: 'all .12s',
                  '&:hover': {borderColor: 'primary.light', bgcolor: (t) => alpha(t.palette.primary.main, 0.03), transform: 'translateY(-1px)'},
                }}
              >
                <Box sx={{minWidth: 0, flex: '1 1 220px'}}>
                  <Typography sx={{fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 700, color: 'text.primary'}} noWrap>
                    {c.code}
                  </Typography>
                  {c.display && c.display !== c.code ? (
                    <Typography sx={{fontSize: 11.5, color: 'text.secondary'}} noWrap>{c.display}</Typography>
                  ) : null}
                </Box>
                <Badge status={st.status} label={st.label} />
                <Typography sx={{fontSize: 12, color: 'text.secondary', minWidth: 90}}>
                  {ov} override{ov === 1 ? '' : 's'}
                </Typography>
                <Typography sx={{fontSize: 11.5, color: 'text.disabled', flex: '1 1 140px', textAlign: {xs: 'left', sm: 'right'}}}>
                  {view && view.lastPublishedAt ? 'Published ' + fmtDate(view.lastPublishedAt) : 'Never published'}
                </Typography>
                <Button
                  size='small'
                  variant='text'
                  endIcon={<ArrowForwardOutlinedIcon style={{fontSize: 14}} />}
                  onClick={(e) => {
                    e.stopPropagation()
                    goEdit(c.code)
                  }}
                  sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600, flexShrink: 0}}
                >
                  Edit zones
                </Button>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

const TABS = [
  {id: 'overview', label: 'Overview', icon: <InfoOutlinedIcon style={ICO} />},
  {id: 'codes', label: 'Courier Codes', icon: <SettingsInputComponentOutlinedIcon style={ICO} />},
  {id: 'agreements', label: 'Agreements', icon: <DescriptionOutlinedIcon style={ICO} />},
  {id: 'zones', label: 'Zone Mapping', icon: <PublicOutlinedIcon style={ICO} />},
  {id: 'ratecards', label: 'Rate Cards', icon: <AttachMoneyOutlinedIcon style={ICO} />},
  {id: 'billing', label: 'Billing', icon: <ReceiptLongOutlinedIcon style={ICO} />},
  {id: 'performance', label: 'Performance', icon: <LeaderboardOutlinedIcon style={ICO} />},
]

export default function Partner360() {
  const {id} = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const [updateParentStatus] = useUpdateParentStatusMutation()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') || 'overview'
  const [tab, setTab] = useState(TABS.some((t) => t.id === initialTab) ? initialTab : 'overview')
  const [chkOpen, setChkOpen] = useState(searchParams.get('chk') === '1')
  const [poc, setPoc] = useState({open: false}) // {open, idx, mode, prefRole}
  const [confirm, setConfirm] = useState(null) // {deact}
  const [editOpen, setEditOpen] = useState(false)

  /* real data: parent + codes + agreement timeline — read via SUBSCRIBED RTK Query
     hooks. The view re-renders automatically whenever a mutation invalidates the
     Parent / Code / Agreement cache (add POC, set COD SLA, edit details, sign an
     agreement…), so the onboarding checklist recomputes instantly with no hard
     refresh. (The previous lazy `.unwrap()` into local state raced with the
     invalidation refetch and could read stale data, which is why a manual refresh
     was needed.) */
  const {data: parentRaw, isLoading: parentLoading, error: parentErr, refetch: refetchParent} = useGetParentQuery(id, {
    skip: !id,
  })
  const {data: codesRaw, isLoading: codesLoading, refetch: refetchCodes} = useCodesByParentQuery(id, {skip: !id})
  const {data: timelineRaw, isLoading: timelineLoading, refetch: refetchTimeline} = useAgreementTimelineQuery(id, {
    skip: !id,
  })

  const p = useMemo(() => (parentRaw ? mapParent(parentRaw) : null), [parentRaw])
  // codesRaw is already the codes ARRAY (unwrap() extracts ResultData.data); not an envelope.
  const codes = useMemo(() => (Array.isArray(codesRaw) ? codesRaw.map(mapCode) : []), [codesRaw])
  const timeline = useMemo(() => timelineRaw || [], [timelineRaw])
  const loading = parentLoading || codesLoading || timelineLoading
  const loadError = parentErr ? apiError(parentErr) : null

  /* manual refresh kept for existing callers; subscribed queries also auto-refetch
     on cache invalidation after any mutation. */
  const load = useCallback(() => {
    refetchParent()
    refetchCodes()
    refetchTimeline()
  }, [refetchParent, refetchCodes, refetchTimeline])

  const [fetchRateCards] = useLazyParentRateCardsQuery()
  const [fetchInvoices] = useLazyCourierInvoicesQuery()
  const [fetchZoneSetup] = useLazyZoneGetSetupQuery()

  /* onboarding-stage data (rate cards + per-code zone setups) — fetched once at the
     top level so the SECTIONS nav badges and the Configuration status strip share a
     single source. undefined = still loading, [] = loaded/empty. */
  const [setupRc, setSetupRc] = useState(undefined)
  const [setupZv, setSetupZv] = useState(undefined)

  useEffect(() => {
    if (!id) return undefined
    let on = true
    setSetupRc(undefined)
    fetchRateCards(id)
      .unwrap()
      .then((d) => on && setSetupRc(Array.isArray(d) ? d : []))
      .catch(() => on && setSetupRc([]))
    return () => {
      on = false
    }
  }, [id, fetchRateCards])

  useEffect(() => {
    let on = true
    const list = codes || []
    setSetupZv(undefined)
    if (!list.length) {
      setSetupZv([])
      return undefined
    }
    Promise.all(list.map((c) => fetchZoneSetup(c.code).unwrap().then(normView).catch(() => null))).then(
      (r) => on && setSetupZv(r.filter(Boolean))
    )
    return () => {
      on = false
    }
  }, [codes, fetchZoneSetup])

  /* lazy tab data: rate cards (GET parent/:id/rateCards) + courier invoices (finance) */
  const [rateCards, setRateCards] = useState(null) // null = not loaded yet
  const [rcLoading, setRcLoading] = useState(false)
  const [rcError, setRcError] = useState(null)
  const [invoices, setInvoices] = useState(null) // null = not loaded yet
  const [invLoading, setInvLoading] = useState(false)
  const [invError, setInvError] = useState(null)

  const loadRateCards = useCallback(() => {
    setRcLoading(true)
    setRcError(null)
    fetchRateCards(id)
      .unwrap()
      .then((data) => setRateCards(data || []))
      .catch((e) => setRcError(apiError(e)))
      .finally(() => setRcLoading(false))
  }, [id, fetchRateCards])

  const parentName = p ? p.name : null
  const loadInvoices = useCallback(() => {
    if (!parentName) return
    setInvLoading(true)
    setInvError(null)
    fetchInvoices({courierParentName: parentName, page: 0, size: 20})
      .unwrap()
      .then((payload) => {
        const rows = payload && Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : []
        setInvoices(rows)
      })
      .catch((e) => setInvError(apiError(e)))
      .finally(() => setInvLoading(false))
  }, [parentName, fetchInvoices])

  useEffect(() => {
    if (tab === 'ratecards' && rateCards == null && !rcLoading && !rcError) loadRateCards()
  }, [tab, rateCards, rcLoading, rcError, loadRateCards])

  useEffect(() => {
    if (tab === 'billing' && invoices == null && !invLoading && !invError) loadInvoices()
  }, [tab, invoices, invLoading, invError, loadInvoices])

  if (loading) {
    return (
      <>
        <PageTitle>{'Courier Partner 360'}</PageTitle>
        <Box sx={{maxWidth: 1100}}>
          <LoadingCard label='Loading partner…' />
        </Box>
      </>
    )
  }

  if (loadError || !p) {
    return (
      <>
        <PageTitle>{'Courier Partner 360'}</PageTitle>
        <EmptyState
          icon={<InboxOutlinedIcon style={{fontSize: 28}} />}
          title='Partner not found'
          body={loadError || `No courier partner exists for “${id}”.`}
          action={
            <Button variant='contained' disableElevation startIcon={<ArrowBackOutlinedIcon style={{fontSize: 16}} />} onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/supply/partners'))} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
              Go Back
            </Button>
          }
        />
      </>
    )
  }

  /* onboarding score — computed from real data, never stored */
  const comps = scoreComponents(p, codes, timeline)
  p.score = Math.min(
    100,
    comps.reduce((s, c) => s + (c.met ? c.weight : 0), 0)
  )

  const sc = scoreColor(p.score)
  const activeCodes = codes.filter((c) => c.status === 'Active').length
  const activeAgrs = timeline.filter(
    (t) => t.agreement && t.agreement.stage === 'SIGNED' && t.agreement.status === 'ACTIVE'
  ).length

  /* per-section onboarding status for the SECTIONS nav badges (loading until the
     rate-card / zone fetches resolve). */
  const total = codes.length
  const sectionStatus = {
    overview: {state: checklistState(comps)},
    codes: {state: codesState(codes)},
    agreements: {state: agreementState(timeline)},
    ratecards: setupRc === undefined ? {loading: true} : {state: rateCardState(setupRc, total)},
    zones: setupZv === undefined ? {loading: true} : {state: zoneState(setupZv, total)},
  }

  const codSlaParts = []
  if (p.codD2C) codSlaParts.push('D2C ' + p.codD2C + 'd')
  if (p.codPTL) codSlaParts.push('PTL ' + p.codPTL + 'd')

  // KPI cards jump to tabs (mockup map: agreements, agreements, codes, codes)
  const kpis = [
    {label: 'Total Agreements', value: timeline.length, icon: <DescriptionOutlinedIcon style={ICO} />, sub: 'all stages & types', onClick: () => setTab('agreements')},
    {label: 'Active Agreements', value: activeAgrs, icon: <CheckCircleOutlinedIcon style={ICO} />, sub: 'signed & governing', onClick: () => setTab('agreements')},
    {label: 'Codes (Active)', value: activeCodes, icon: <SettingsInputComponentOutlinedIcon style={ICO} />, onClick: () => setTab('codes')},
    {label: 'Codes (Total)', value: codes.length, icon: <SettingsInputComponentOutlinedIcon style={ICO} />, onClick: () => setTab('codes')},
    {label: 'COD SLA', value: codSlaParts.join(' · ') || '—', icon: <AttachMoneyOutlinedIcon style={ICO} />, accent: '#1976D2'},
  ]

  const openPoc = (idx, mode, prefRole) => {
    // mode 'basics' from "Edit" → opens the Edit Details drawer
    if (mode === 'basics') {
      setEditOpen(true)
      return
    }
    setPoc({open: true, idx: idx != null ? idx : null, mode: mode || 'view', prefRole})
  }

  const onChecklistFix = (act) => {
    if (act === 'poc') openPoc(null, 'add')
    else if (act === 'agreement') {
      setChkOpen(false)
      // route to the Upload Agreement screen (drawer pre-set to this partner) so
      // the user can upload the signed agreement directly, rather than the queue.
      navigate('/supply/agreements?upload=' + p.id)
    } else if (act === 'codes') {
      setChkOpen(false)
      setTab('codes')
    } else if (act === 'basics' || act === 'cod') setEditOpen(true)
    else toast('Open the relevant step to complete this item.', 'info')
  }

  const doToggle = (reason) => {
    const deact = p.status === 'Active'
    updateParentStatus({id: p.id, status: deact ? 'INACTIVE' : 'ACTIVE', reason: reason || undefined})
      .unwrap()
      .then(() => {
        toast(p.name + (deact ? ' deactivated' : ' activated') + '. Audit recorded.', 'success')
        load()
      })
      .catch((e) => toast(apiError(e), 'error'))
  }

  return (
    <>
      <PageTitle>{p.name + ' · 360'}</PageTitle>

      <Button
        size='small'
        startIcon={<ArrowBackOutlinedIcon style={{fontSize: 16}} />}
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/supply/partners'))}
        sx={{mb: 1.5, textTransform: 'none', fontWeight: 600, color: 'text.secondary', px: 0.5}}
      >
        Back
      </Button>

      {/* hero — dark banner matching the rest of Courier Setup */}
      <Box sx={{position: 'relative', overflow: 'hidden', borderRadius: '12px', mb: 3, background: 'linear-gradient(135deg, #0d1b2a 0%, #1b3a5c 60%, #0d2137 100%)'}}>
        <Box sx={{position: 'absolute', right: -60, top: -60, width: 280, height: 280, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)', pointerEvents: 'none'}} />
        <Box sx={{position: 'relative', p: {xs: 2.5, md: 3.5}}}>
          <Stack direction={{xs: 'column', md: 'row'}} spacing={3} justifyContent='space-between' alignItems={{md: 'flex-start'}}>
            {/* identity */}
            <Stack direction='row' spacing={2.25} alignItems='flex-start' sx={{minWidth: 0}}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '18px',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #2065D1, #1B4FA8)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 21,
                  fontWeight: 800,
                  boxShadow: '0 6px 16px rgba(32,101,209,.45)',
                }}
              >
                {initials(p.name)}
              </Box>
              <Box sx={{minWidth: 0}}>
                <Stack direction='row' spacing={1.25} alignItems='center' flexWrap='wrap'>
                  <Typography sx={{fontSize: 24, fontWeight: 800, color: '#fff', letterSpacing: '-.4px'}}>{p.name}</Typography>
                  <StatusChip status={p.status} reason={p.statusReason} />
                </Stack>
                <Typography sx={{mt: 0.5, fontSize: 13.5, color: 'rgba(255,255,255,.6)'}}>{p.legal}</Typography>
                <Stack direction='row' spacing={0.75} alignItems='center' flexWrap='wrap' useFlexGap sx={{mt: 1.25}}>
                  <VertChips verticals={p.verticals} />
                  <Chip size='small' icon={<SettingsInputComponentOutlinedIcon style={{fontSize: 13, color: 'rgba(255,255,255,.7)'}} />} label={p.integration || 'No integration'} sx={{height: 22, fontSize: 11.5, fontWeight: 600, color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: 1, borderColor: 'rgba(255,255,255,.2)', '& .MuiChip-icon': {ml: 0.75}}} />
                  <Chip size='small' label={'GST ' + (p.gst || '—')} sx={{height: 22, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: '#fff', bgcolor: 'rgba(255,255,255,.12)', border: 1, borderColor: 'rgba(255,255,255,.2)'}} />
                </Stack>
              </Box>
            </Stack>

            {/* onboarding gauge + actions */}
            <Stack direction='row' spacing={2.5} alignItems='center' sx={{flexShrink: 0, width: {xs: '100%', md: 'auto'}}}>
              <Gauge value={p.score} color={sc} onDark />
              <Stack spacing={1} sx={{minWidth: 150, flex: {xs: 1, md: 'none'}}}>
                <Button size='small' variant='outlined' startIcon={<CheckIcon />} onClick={() => setChkOpen((o) => !o)} sx={{...HERO_BTN, justifyContent: 'flex-start'}}>
                  Checklist
                </Button>
                <Button size='small' variant='outlined' startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)} sx={{...HERO_BTN, justifyContent: 'flex-start'}}>
                  Edit details
                </Button>
                <Button size='small' variant='contained' disableElevation startIcon={<PowerSettingsNewOutlinedIcon />} onClick={() => setConfirm({deact: p.status === 'Active'})} sx={p.status === 'Active' ? {...HERO_BTN_SOLID, background: '#FCA5A5', color: '#7F1D1D', justifyContent: 'flex-start', '&:hover': {background: '#F87171', boxShadow: 'none'}} : {...HERO_BTN_SOLID, justifyContent: 'flex-start'}}>
                  {p.status === 'Active' ? 'Deactivate' : 'Activate'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* onboarding checklist — expands below the header, not inside it */}
      {chkOpen ? (
        <Box sx={{mb: 2, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 2px rgba(15,23,42,.04)', p: {xs: 2, md: 2.5}}}>
          <ChecklistPanel p={p} comps={comps} onFix={onChecklistFix} />
        </Box>
      ) : null}

      {/* deactivation banner */}
      {p.status === 'Inactive' && p.statusReason ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            mb: 2,
            p: 1.75,
            borderRadius: 2,
            border: 1,
            borderColor: (t) => alpha(t.palette.error.main, 0.3),
            bgcolor: (t) => alpha(t.palette.error.main, 0.06),
            color: 'error.dark',
          }}
        >
          <WarningAmberOutlinedIcon sx={{fontSize: 18, flexShrink: 0, mt: 0.25}} />
          <Typography sx={{fontSize: 13}}>
            This partner is deactivated. Reason: {p.statusReason}. All codes remain in their current state.
          </Typography>
        </Box>
      ) : null}

      <SetupStatusStrip total={codes.length} rc={setupRc} zv={setupZv} />

      <Box sx={{display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2}}>
        {kpis.map((k) => (
          <StatCard key={k.label} label={k.label} value={k.value} sub={k.sub} icon={k.icon} accent={k.accent || '#2065D1'} onClick={k.onClick} />
        ))}
      </Box>

      {/* section menu */}
      <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: 2, alignItems: 'flex-start'}}>
        <Box
          component='nav'
          sx={{
            width: {xs: '100%', md: 250},
            flexShrink: 0,
            position: {md: 'sticky'},
            top: {md: 16},
            border: 1,
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: '0 1px 2px rgba(15,23,42,.04)',
            p: 1,
            display: 'flex',
            flexDirection: {xs: 'row', md: 'column'},
            gap: 0.5,
            overflowX: {xs: 'auto', md: 'visible'},
          }}
        >
          <SectionLabel sx={{px: 1.25, pt: 0.75, pb: 0.25, display: {xs: 'none', md: 'block'}}}>Sections</SectionLabel>
          {TABS.map((t) => {
            const pill = t.id === 'codes' ? codes.length : t.id === 'agreements' ? timeline.length : null
            const ss = sectionStatus[t.id]
            const active = tab === t.id
            return (
              <Box
                key={t.id}
                component='button'
                onClick={() => setTab(t.id)}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  width: '100%',
                  cursor: 'pointer',
                  border: 'none',
                  borderRadius: 2.5,
                  px: 1.25,
                  py: 1,
                  font: 'inherit',
                  fontSize: 13.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? 'primary.dark' : 'text.secondary',
                  whiteSpace: 'nowrap',
                  transition: 'all .14s',
                  bgcolor: active ? (th) => alpha(th.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': {bgcolor: active ? (th) => alpha(th.palette.primary.main, 0.1) : 'action.hover', color: active ? 'primary.dark' : 'text.primary'},
                }}
              >
                {active ? <Box sx={{position: 'absolute', left: 0, top: 9, bottom: 9, width: 3, borderRadius: 99, bgcolor: 'primary.main'}} /> : null}
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '9px',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: active ? 'primary.main' : 'action.hover',
                    color: active ? '#fff' : 'text.secondary',
                    transition: 'all .14s',
                  }}
                >
                  {t.icon}
                </Box>
                <Box component='span' sx={{flex: 1, textAlign: 'left'}}>{t.label}</Box>
                {ss && !ss.loading ? <StatusPill state={ss.state} title={'Onboarding: ' + (ss.state === 'done' ? 'complete' : ss.state === 'inprogress' ? 'in progress' : 'pending')} /> : null}
                {pill != null ? (
                  <Box component='span' sx={{fontSize: 11, fontWeight: 700, bgcolor: active ? 'primary.main' : 'action.hover', color: active ? '#fff' : 'text.secondary', borderRadius: 99, px: 0.9, py: 0.1, minWidth: 20, textAlign: 'center'}}>
                    {pill}
                  </Box>
                ) : null}
              </Box>
            )
          })}
        </Box>
        <Box sx={{flex: 1, minWidth: 0, width: '100%'}}>
          {tab === 'overview' ? <OverviewTab p={p} onOpenPoc={openPoc} /> : null}
          {tab === 'codes' ? <CodesTab p={p} codes={codes} timeline={timeline} navigate={navigate} /> : null}
          {tab === 'agreements' ? <AgreementsTab p={p} timeline={timeline} toast={toast} /> : null}
          {tab === 'zones' ? <ZonesTab p={p} codes={codes} navigate={navigate} /> : null}
          {tab === 'ratecards' ? (
            <RateCardsTab p={p} cards={rateCards} loading={rcLoading} error={rcError} onRetry={loadRateCards} navigate={navigate} />
          ) : null}
          {tab === 'billing' ? (
            <BillingTab p={p} invoices={invoices} loading={invLoading} error={invError} onRetry={loadInvoices} />
          ) : null}
          {tab === 'performance' ? <PerformanceTab /> : null}
        </Box>
      </Box>

      {/* POC slide-over (view / edit / add) — saves via PUT contacts (full replace) */}
      <PocDrawer
        p={p}
        state={poc}
        onClose={() => setPoc({open: false})}
        onChanged={() => {
          setPoc({open: false})
          load()
        }}
        toast={toast}
      />

      {/* Edit Details drawer — PATCH partial details */}
      <EditDetailsDrawer
        p={p}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={load}
        toast={toast}
      />

      {/* activate / deactivate */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={confirm ? `${confirm.deact ? 'Deactivate' : 'Activate'} ${p.name}?` : ''}
        body={
          confirm
            ? confirm.deact
              ? `This will immediately remove ${p.name} from PACE allocation.`
              : `Restore ${p.name} and its codes to allocation eligibility.`
            : ''
        }
        bullets={
          confirm && confirm.deact
            ? [
                'Remove from PACE allocation',
                'Flag all active shipments on this partner’s codes for manual review',
                'Codes remain in their current state until individually managed',
              ]
            : undefined
        }
        needReason={!!(confirm && confirm.deact)}
        danger={!!(confirm && confirm.deact)}
        okLabel={confirm && !confirm.deact ? 'Activate' : 'Deactivate'}
        onOk={(reason) => {
          doToggle(reason)
          setConfirm(null)
        }}
      />
    </>
  )
}
